import React, { useState, useRef, useEffect } from "react";
import FeedbackBar from "./FeedbackBar";

export default function ChatPanel({ isOpen, onClose, messages, loading, onAsk }) {
  const [input, setInput] = useState("");
  const bodyRef = useRef(null);
  const endRef = useRef(null);

  // Helper: stick to bottom
  const scrollToBottom = (smooth = true) => {
    // Prefer an anchor element so we always land exactly at the end
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
      return;
    }
    // Fallback: scroll the container
    if (bodyRef.current) {
      const el = bodyRef.current;
      el.scrollTop = el.scrollHeight;
    }
  };

  // When panel opens, jump to bottom immediately (no animation)
  useEffect(() => {
    if (isOpen) {
      // allow the panel to mount/paint first
      const id = setTimeout(() => scrollToBottom(false), 0);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  // On new messages or loading indicator changes, smooth scroll
  useEffect(() => {
    if (!isOpen) return;
    // small delay lets the new message render and size itself
    const id = setTimeout(() => scrollToBottom(true), 0);
    return () => clearTimeout(id);
  }, [messages, loading, isOpen]);

  if (!isOpen) return null;



  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        bottom: 88,
        width: 460,
        height: 560,
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        boxShadow: "0 12px 28px rgba(0,0,0,0.2)",
      }}
    >
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #f3f4f6", fontWeight: 600 }}>
        Machine Tracking Assistant
        <button
          onClick={onClose}
          style={{ float: "right", border: "none", background: "transparent", cursor: "pointer" }}
          title="Close"
        >
          ✕
        </button>
      </div>

      <div ref={bodyRef} style={{ flex: 1, overflowY: "auto", padding: 12 }}>
        {messages.map((m, i) => {
          const isAssistant = m.from === "assistant";
          // Find the most recent user message before this assistant message
          const prevUser = isAssistant
            ? [...messages].slice(0, i).reverse().find(x => x.from === "user")
            : null;

          return (
            <div key={i} style={{ marginBottom: 8, textAlign: m.from === "user" ? "right" : "left" }}>
              <div
                style={{
                  display: "inline-block",
                  background: m.from === "user" ? "#2563eb" : "#f3f4f6",
                  color: m.from === "user" ? "white" : "black",
                  padding: "6px 10px",
                  borderRadius: 8,
                  maxWidth: "80%",
                  whiteSpace: "pre-wrap",
                }}
              >
                {m.text}
              </div>
              {isAssistant && (
                <FeedbackBar
                  disabled={!!m.feedbackSubmitted}
                  question={prevUser ? prevUser.text : ""}
                  answer={m.text}
                  meta={m.meta || {}}
                  onSubmitted={(verdict) => {
                    // mark this message so buttons disable on re-render
                    m.feedbackSubmitted = true;
                  }}
                />
              )}
            </div>
          );
        })}
        {loading && <div style={{ color: "#6b7280", fontSize: 14 }}>Thinking…</div>}
        {/* anchor to ensure we always scroll to the very end */}
        <div ref={endRef} />
      </div>

      <div style={{ borderTop: "1px solid #e5e7eb", padding: 8 }}>
        <input
          style={{ width: "70%", border: "1px solid #e5e7eb", borderRadius: 6, padding: 6 }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about a shift…"
          onKeyDown={(e) => e.key === "Enter" && (onAsk(input), setInput(""))}
        />
        <button
          onClick={() => {
            onAsk(input);
            setInput("");
          }}
          style={{
            marginLeft: 8,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #111827",
            background: "#111827",
            color: "white",
            cursor: "pointer",
          }}
        >
          Send
        </button>
      </div>

      <div style={{ borderTop: "1px solid #f3f4f6", padding: 8 }}>
        <button onClick={() => onAsk("Summarize Shift 1 yesterday")}>Summarize Yesterday</button>
        <button style={{ marginLeft: 8 }} onClick={() => onAsk("Top downtime reasons yesterday on Shift 1")}>
          Top Downtime
        </button>
        {/* <button style={{ marginLeft: 8 }} onClick={() => onAsk("Biggest week over week production increase")}>
          Biggest WoW Gain
        </button> */}
      </div>
    </div>
  );
}
